import React, { useContext, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { getStatusColor } from '../../../../components/Kanban/KanbanTab'
import { SectionHeader } from '../../vehicles/VehicleComponents/SectionKit'
import { isSiteAdmin } from '../../makeReadyModule/makeReadyAccess'
import { AssetContext } from './SingleAsset'

const PRIORITY = {
  3: { label: 'High', color: 'error' },
  2: { label: 'Normal', color: 'info' },
  1: { label: 'Low', color: 'success' },
}

const DefectsSection = () => {
  const { asset } = useContext(AssetContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  // null = the active defects that came with the asset; an array = the full
  // history fetched on demand.
  const [allDefects, setAllDefects] = useState(null)
  const [loadingAll, setLoadingAll] = useState(false)

  // The full-history route is Governance only.
  const teams = user?.employee?.teams || user?.teams || []
  const canSeeHistory = isSiteAdmin(user) || teams.includes('Governance')

  const showingAll = allDefects !== null
  const rows = showingAll ? allDefects : (asset.currentDefects ?? [])

  const loadAll = () => {
    setLoadingAll(true)
    API.get('/defects/asset/' + asset.assetID)
      .then((res) => {
        if (res.status === 200) {
          setAllDefects(res.data ?? [])
        } else {
          setToast({
            message: 'Unable to load defect history. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to load defect history. Please try again later.',
          severity: 'error',
        }),
      )
      .finally(() => setLoadingAll(false))
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Defects"
        caption={
          showingAll
            ? 'Every defect reported against this asset, including completed ones'
            : 'Active defects reported against this asset'
        }
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canSeeHistory && (
              <Button
                size="small"
                onClick={() => (showingAll ? setAllDefects(null) : loadAll())}
                disabled={loadingAll}
              >
                {showingAll ? 'Active only' : 'Include completed'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                navigate(`/report-defect?assetIDLabel=${asset.assetID}`)
              }
            >
              Report Defect
            </Button>
          </Stack>
        }
      />
      {loadingAll ? (
        <LinearProgress />
      ) : rows.length === 0 ? (
        <Alert severity={showingAll ? 'info' : 'success'}>
          {showingAll
            ? 'No defects have been recorded against this asset.'
            : 'No active defects on this asset.'}
        </Alert>
      ) : (
        <CustomDataGrid
          rows={rows}
          getRowId={(row) => row._id}
          columns={[
            {
              field: 'createdAt',
              headerName: 'Date',
              minWidth: 110,
              valueGetter: (value) => (value ? moment(value).toDate() : null),
              renderCell: ({ value }) =>
                value ? moment(value).format('DD/MM/YYYY') : '',
            },
            {
              field: 'priority',
              headerName: 'Priority',
              minWidth: 110,
              renderCell: ({ value }) => {
                const priority = PRIORITY[value] ?? PRIORITY[2]
                return (
                  <Chip
                    size="small"
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                    color={priority.color}
                    label={priority.label}
                  />
                )
              },
            },
            {
              field: 'summary',
              headerName: 'Summary',
              flex: 1,
              minWidth: 220,
              valueGetter: (value, row) => value || row.description || '',
            },
            {
              field: 'status',
              headerName: 'Status',
              minWidth: 150,
              renderCell: ({ value }) =>
                value ? (
                  <Chip
                    size="small"
                    sx={{ borderRadius: 1, width: '100%' }}
                    label={value}
                    color={getStatusColor(value)}
                  />
                ) : null,
            },
            {
              field: 'assignedTo',
              headerName: 'Assigned To',
              minWidth: 180,
              valueGetter: (value) =>
                value?.firstName ? `${value.firstName} ${value.lastName}` : '',
              renderCell: ({ row }) =>
                row.assignedTo?.firstName ? (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Avatar
                      src={row.assignedTo.photograph}
                      sx={{ width: 24, height: 24, borderRadius: 1 }}
                    />
                    <Typography variant="body2">
                      {row.assignedTo.firstName} {row.assignedTo.lastName}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    Unassigned
                  </Typography>
                ),
            },
          ]}
          disableRowSelectionOnClick
          onRowClick={({ row }) => navigate('/defects/' + row._id)}
          slots={{ toolbar: () => null, footer: () => null }}
        />
      )}
    </Stack>
  )
}

export default DefectsSection
